import { useState, type ReactNode } from "react";
import { authService } from "../../features";
import { FACEBOOK_SIGN_IN } from "../../lib/featureFlags";
import { Button } from "../ui";

type Provider = "google" | "facebook";

const providers: { id: Provider; label: string; icon: ReactNode }[] = [
  {
    id: "google",
    label: "Continue with Google",
    icon: (
      <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24">
        <path
          d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.87h5.39a4.6 4.6 0 0 1-2 3.02v2.5h3.24c1.9-1.75 2.97-4.32 2.97-7.34Z"
          fill="#4285F4"
        />
        <path
          d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.24-2.5c-.9.6-2.04.95-3.38.95-2.6 0-4.8-1.76-5.59-4.12H3.07v2.58A10 10 0 0 0 12 22Z"
          fill="#34A853"
        />
        <path
          d="M6.41 13.9a6 6 0 0 1 0-3.8V7.52H3.07a10 10 0 0 0 0 8.96l3.34-2.58Z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2a10 10 0 0 0-8.93 5.52l3.34 2.58C7.2 7.74 9.4 5.98 12 5.98Z"
          fill="#EA4335"
        />
      </svg>
    ),
  },
  {
    id: "facebook",
    label: "Continue with Facebook",
    icon: (
      <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24">
        <path
          d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.95h-1.51c-1.49 0-1.96.93-1.96 1.88V12h3.34l-.53 3.47h-2.81v8.38A12 12 0 0 0 24 12Z"
          fill="#1877F2"
        />
      </svg>
    ),
  },
];

const enabledProviders = providers.filter(
  ({ id }) => id !== "facebook" || FACEBOOK_SIGN_IN,
);

type SocialSignInProps = {
  disabled?: boolean;
  onError: (message: string) => void;
};

/** Google and Facebook buttons, above the email form, with an "or" divider. */
export const SocialSignIn = ({ disabled, onError }: SocialSignInProps) => {
  const [pending, setPending] = useState<Provider | null>(null);

  const start = async (provider: Provider) => {
    setPending(provider);
    const error = await authService.signInWithProvider(provider);
    // On success the browser is already navigating to the provider.
    if (error) {
      onError(error);
      setPending(null);
    }
  };

  return (
    <div className="mb-7">
      <div className="space-y-3">
        {enabledProviders.map(({ id, label, icon }) => (
          <Button
            className="gap-3"
            disabled={disabled || pending !== null}
            fullWidth
            key={id}
            onClick={() => void start(id)}
            size="lg"
          >
            {icon}
            {pending === id ? "Redirecting…" : label}
          </Button>
        ))}
      </div>
      <div className="mt-7 flex items-center gap-4 text-xs text-hazumu-muted">
        <span className="h-px flex-1 bg-hazumu-clay/40" />
        or
        <span className="h-px flex-1 bg-hazumu-clay/40" />
      </div>
    </div>
  );
};
